/**
 * CORE-831 — где сейчас находится фоновый трек эфира (чистая функция).
 *
 * Сервер (CORE-1144) намеренно НЕ шлёт позицию: у неё нет своего поля и нет
 * своего потока. Есть только точка отсчёта — когда трек начали — и сумма пауз.
 * Позицию каждый клиент считает сам, поэтому поздний вход и реконнект попадают
 * туда же, где все остальные, а не догоняют «текущую позицию», отправленную
 * секунду назад.
 *
 * Модуль отдельный и чистый по той же причине, что `roomMediaSwapPolicy` и
 * `backgroundAudioVolume`: правило проверяется тестом без медиаэлемента, и
 * ровно одно определение позиции используют и панель мастера, и плеер зрителя.
 * Разные определения — это и есть рассинхрон.
 */

/** Состояние фона эфира — форма приходит и снимком в ответе эфира
 *  (`live.backgroundAudio`), и событием хаба `BackgroundAudioChanged`. */
export interface LiveBackgroundAudioState {
    liveId: number;
    soundTrackId: number | null;
    title: string | null;
    coverImageUrl: string | null;
    playbackUrl: string | null;
    durationSeconds: number | null;
    /** ISO-время старта трека. null — фон ни разу не запускали или остановили. */
    startedAtUtc: string | null;
    isPaused: boolean;
    pausedAtUtc: string | null;
    totalPausedSeconds: number;
    isLooping: boolean;
    /** Мастер-громкость 0..1, общая для всего эфира. */
    masterVolume: number;
    /** none | playing | paused | ended — как его видит сервер. */
    status: string;
    statusChangedAtUtc: string | null;
}

export interface BackgroundAnchorResult {
    /** Секунды от начала трека. Для зацикленного — уже по модулю длительности. */
    positionSeconds: number;
    /** Незацикленный трек доиграл до конца. Панель показывает «Завершён». */
    ended: boolean;
    /** Элемент должен звучать прямо сейчас. */
    shouldPlay: boolean;
}

const parseMs = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const ms = Date.parse(iso);
    return Number.isFinite(ms) ? ms : null;
};

/**
 * Позиция и намерение для фонового элемента.
 *
 * Прошло = (момент отсчёта времени − старт) − накопленные паузы, где «момент
 * отсчёта» — это сейчас, а на паузе — время постановки на паузу. Именно так
 * пауза «держит позицию», не имея своего поля позиции.
 *
 * Зацикленный трек берётся по модулю длительности: у него нет конца, и вошедший
 * на двадцатой минуте пятиминутного трека должен попасть в тот же виток, что и
 * все. Незацикленный, перешагнувший длительность, — «Завершён»: позиция
 * прижимается к концу, играть больше нечего.
 *
 * Длительность 0 или null (метаданные не доехали) НЕ считается концом трека:
 * иначе фон гас бы на ровном месте до загрузки метаданных.
 */
export function resolveBackgroundAnchor(
    state: LiveBackgroundAudioState | null | undefined,
    nowMs: number = Date.now(),
): BackgroundAnchorResult {
    const idle: BackgroundAnchorResult = { positionSeconds: 0, ended: false, shouldPlay: false };
    if (!state || !state.soundTrackId) return idle;

    const startedMs = parseMs(state.startedAtUtc);
    if (startedMs === null) return idle;

    const untilMs = state.isPaused ? (parseMs(state.pausedAtUtc) ?? nowMs) : nowMs;
    const elapsed = (untilMs - startedMs) / 1000 - (state.totalPausedSeconds || 0);
    const raw = Math.max(0, elapsed);

    const duration = state.durationSeconds && state.durationSeconds > 0 ? state.durationSeconds : null;

    if (state.isLooping) {
        return {
            positionSeconds: duration ? raw % duration : raw,
            ended: false,
            shouldPlay: !state.isPaused,
        };
    }

    if (duration !== null && raw >= duration) {
        return { positionSeconds: duration, ended: true, shouldPlay: false };
    }

    return { positionSeconds: raw, ended: false, shouldPlay: !state.isPaused };
}

/**
 * Ведущий нажал «Стоп»: трек выбран, отсчёта нет, а команда уже была
 * (`statusChangedAtUtc` стоит). Сервер в этот момент отдаёт `status: "none"`
 * и `startedAtUtc: null`. Без этого признака остановленный трек читался как
 * «Пауза» (возврат QA 02.10): отличить стоп от паузы было нельзя. До первой
 * команды `statusChangedAtUtc` пуст — это ещё не стоп, а «не запускали».
 */
export function isBackgroundStopped(state: LiveBackgroundAudioState | null | undefined): boolean {
    return !!state && !!state.soundTrackId && !state.startedAtUtc && !!state.statusChangedAtUtc;
}

/** Подпись состояния для панели. Совпадает с набором из тикета:
 *  Не выбран / Играет / Пауза / Завершён / Ошибка. «Загрузка» добавляет уже
 *  элемент — сервер про буферизацию клиента ничего не знает. */
export type LiveBackgroundPanelStatus = 'none' | 'playing' | 'paused' | 'ended' | 'error' | 'loading';

export const BACKGROUND_STATUS_LABELS: Record<LiveBackgroundPanelStatus, string> = {
    none: 'Трек не выбран',
    playing: 'Играет',
    paused: 'Пауза',
    ended: 'Завершён',
    error: 'Ошибка воспроизведения',
    loading: 'Загрузка',
};

export interface PanelStatusInput {
    state: LiveBackgroundAudioState | null | undefined;
    /** Незацикленный трек доиграл — из <see cref="resolveBackgroundAnchor"/>. */
    ended: boolean;
    /** Элемент не смог загрузить файл. */
    errored: boolean;
    /** Элемент ещё не добуферизовался, а играть уже должен. */
    loading: boolean;
}

/**
 * Состояние для панели. Ошибка и конец трека перебивают намерение сервера:
 * трек, который не загрузился или доиграл, не «играет», что бы ни говорил
 * последний статус.
 */
export function resolveLiveBackgroundStatus({ state, ended, errored, loading }: PanelStatusInput): LiveBackgroundPanelStatus {
    if (!state || !state.soundTrackId) return 'none';
    if (errored) return 'error';
    if (ended || isBackgroundStopped(state)) return 'ended';
    if (state.isPaused) return 'paused';
    if (!state.startedAtUtc) return 'paused';
    if (loading) return 'loading';
    return 'playing';
}

/** «05:35» — подпись позиции и длительности на панели. */
export function formatClock(seconds: number | null | undefined): string {
    if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return '--:--';
    const total = Math.floor(seconds);
    const mm = Math.floor(total / 60);
    const ss = total % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}
